import { Circle, CheckCircle, PlayCircle, X, AlertCircle, Clock, Calendar, CalendarCheck } from 'lucide-react';

/**
 * Job Status Constants
 *
 * Definições centralizadas dos 8 status oficiais do InventoryJob.
 * Fonte da verdade: backend/django_project/inventory/models/job.py (STATUS_CHOICES)
 */

// Status codes (usar estes valores ao enviar para a API)
export const JOB_STATUS = {
  PRE_AGENDADA: 'PRE_AGENDADA',
  AGENDADA: 'AGENDADA',
  REAGENDADA: 'REAGENDADA',
  CONFIRMADA: 'CONFIRMADA',
  EM_EXECUCAO: 'EM_EXECUCAO',
  EXECUTADA: 'EXECUTADA',
  NAO_AUTORIZADA: 'NAO_AUTORIZADA',
  NAO_EXECUTADA: 'NAO_EXECUTADA',
  CANCELADA: 'CANCELADA',
} as const;

// Type helper
export type JobStatus = typeof JOB_STATUS[keyof typeof JOB_STATUS];

// Configuração completa de cada status (label, cor, ícone)
// Cores com maior contraste para melhor visibilidade no Gantt Chart
export const STATUS_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  [JOB_STATUS.PRE_AGENDADA]: {
    label: 'Pré-Agendada',
    color: 'bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-100',
    icon: Clock
  },
  [JOB_STATUS.AGENDADA]: {
    label: 'Agendada',
    color: 'bg-blue-200 dark:bg-blue-800 text-blue-900 dark:text-blue-100',
    icon: Calendar
  },
  // N21c: REAGENDADA existia no backend desde 02/2026 mas faltava aqui —
  // vocabulário 1:1 com JOB_STATUS_CHOICES (job_constants.py).
  [JOB_STATUS.REAGENDADA]: {
    label: 'Reagendada',
    color: 'bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100',
    icon: Calendar
  },
  [JOB_STATUS.CONFIRMADA]: {
    label: 'Confirmada',
    color: 'bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100',
    icon: CalendarCheck
  },
  [JOB_STATUS.EM_EXECUCAO]: {
    label: 'Em Execução',
    color: 'bg-purple-200 dark:bg-purple-800 text-purple-900 dark:text-purple-100',
    icon: PlayCircle
  },
  [JOB_STATUS.EXECUTADA]: {
    label: 'Executada',
    color: 'bg-green-200 dark:bg-green-800 text-green-900 dark:text-green-100',
    icon: CheckCircle
  },
  [JOB_STATUS.NAO_AUTORIZADA]: {
    label: 'Não Autorizada',
    color: 'bg-orange-200 dark:bg-orange-800 text-orange-900 dark:text-orange-100',
    icon: AlertCircle
  },
  [JOB_STATUS.NAO_EXECUTADA]: {
    label: 'Não Executada',
    color: 'bg-red-200 dark:bg-red-800 text-red-900 dark:text-red-100',
    icon: X
  },
  // N78 (Alana): Cancelada em vermelho na agenda. Tom mais forte que o
  // vermelho claro de NAO_EXECUTADA acima — os dois precisam ser distinguíveis.
  [JOB_STATUS.CANCELADA]: {
    label: 'Cancelada',
    color: 'bg-red-500 dark:bg-red-700 text-white dark:text-red-50',
    icon: X
  },
};

// Array de todos os status para uso em loops/selects
export const ALL_STATUS = Object.keys(STATUS_CONFIG) as JobStatus[];

// Helper function para obter label de um status
export const getStatusLabel = (status: string): string => {
  return STATUS_CONFIG[status]?.label || status;
};

// Helper function para obter configuração completa de um status
export const getStatusConfig = (status: string) => {
  return STATUS_CONFIG[status] || {
    label: status,
    color: 'bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-100',
    icon: Circle,
  };
};

// Opções de status para filtros (MultiSelectFilter)
export const STATUS_FILTER_OPTIONS = ALL_STATUS.map(status => ({
  value: status,
  label: STATUS_CONFIG[status].label
}));

// Helper para obter cor do status (retorna string de classes CSS)
export const getStatusColor = (status: string): string => {
  return STATUS_CONFIG[status]?.color || STATUS_CONFIG[JOB_STATUS.PRE_AGENDADA].color;
};

// Mapeamento Backend (EN) → Frontend (PT) para compatibilidade
export const STATUS_MAP: Record<string, string> = Object.entries(STATUS_CONFIG).reduce(
  (acc, [key, config]) => ({ ...acc, [key]: config.label }),
  {}
);

// Mapeamento Frontend (PT) → Backend (EN) para compatibilidade
export const STATUS_MAP_REVERSE: Record<string, string> = Object.entries(STATUS_MAP).reduce(
  (acc, [key, value]) => ({ ...acc, [value]: key }),
  {}
);

// Helper para verificar se é status negativo (requer motivo de cancelamento)
export const isNegativeStatus = (status: string): boolean => {
  const negativeStatuses: string[] = [JOB_STATUS.NAO_AUTORIZADA, JOB_STATUS.NAO_EXECUTADA, JOB_STATUS.CANCELADA];
  return negativeStatuses.includes(status.toUpperCase());
};
